import { AcspFormOutcome, getAcspFormOutcome } from "services/director/acspFormOutcome";
import { DIRECTOR_CH_VERIFICATION_STATUS, type DirectorData } from "services/director/directorTypes";
import type { DirectorVerificationStatusData } from "services/directorCodeModal/directorCodeModalTypes";

export enum TeamMemberChVerificationState {
  /** Their ID check matched another person on this application */
  DuplicatedIdentity = "DuplicatedIdentity",
  CodeAdded = "CodeAdded",
  WrongEmail = "WrongEmail",
  /** Companies House already saw a verification for someone with their name and date of birth */
  AcspDuplicateIdentity = "AcspDuplicateIdentity",
  /** Their email is a Hide My Email address, which Companies House can't use */
  ApplePrivateRelayEmail = "ApplePrivateRelayEmail",
  /** The ACSP form failed for another reason, or registration refused it before sending (no reason) */
  Failed = "Failed",
  CodeSent = "CodeSent",
  /** ID check passed and the ACSP form is registered, but not sent to Companies House yet */
  CodeRequested = "CodeRequested",
  /** Waiting for them to pass the ID check */
  IdPending = "IdPending",
  NotStarted = "NotStarted",
}

type TeamMemberFields = Partial<
  Pick<DirectorData, "chPersonalCode" | "identityVerificationStatus" | "identityVerificationFailReason">
>;

const ACSP_FORM_OUTCOME_STATES: Record<AcspFormOutcome, TeamMemberChVerificationState | undefined> = {
  [AcspFormOutcome.WrongEmail]: TeamMemberChVerificationState.WrongEmail,
  [AcspFormOutcome.DuplicateIdentity]: TeamMemberChVerificationState.AcspDuplicateIdentity,
  [AcspFormOutcome.ApplePrivateRelayEmail]: TeamMemberChVerificationState.ApplePrivateRelayEmail,
  [AcspFormOutcome.Failed]: TeamMemberChVerificationState.Failed,
  [AcspFormOutcome.Sent]: TeamMemberChVerificationState.CodeSent,
  [AcspFormOutcome.Pending]: TeamMemberChVerificationState.CodeRequested,
  [AcspFormOutcome.NotRequested]: undefined,
};

/**
 * The director's own status and fail reason say where their ACSP form stands; `GET /idv` is only
 * read for whether their ID check has started, which comes before any status exists.
 */
export const getTeamMemberChVerificationState = (
  director: TeamMemberFields | undefined,
  idvStatus?: Pick<DirectorVerificationStatusData, "isIdvStarted">,
): TeamMemberChVerificationState => {
  const status = director?.identityVerificationStatus;

  // A duplicate outranks a code - that code was issued against somebody else's ID check
  if (status === DIRECTOR_CH_VERIFICATION_STATUS.FAILED_DUPLICATED_IDENTITY) {
    return TeamMemberChVerificationState.DuplicatedIdentity;
  }
  if (director?.chPersonalCode) {
    return TeamMemberChVerificationState.CodeAdded;
  }
  // A duplicate is being sent a fresh ID check link
  if (status === DIRECTOR_CH_VERIFICATION_STATUS.DEDUPLICATION_IN_PROGRESS) {
    return TeamMemberChVerificationState.IdPending;
  }

  const formState = ACSP_FORM_OUTCOME_STATES[getAcspFormOutcome(director)];
  if (formState) {
    return formState;
  }
  return idvStatus?.isIdvStarted ? TeamMemberChVerificationState.IdPending : TeamMemberChVerificationState.NotStarted;
};
